/**
 * ZARISPIRE - mobile menu accordion.
 *
 * Converts Craft's slide-in submenu into an in-place accordion on mobile
 * (<=749px). It fully owns submenu open/close on a capture-phase listener,
 * so Craft's per-summary handler is bypassed for submenus - but the main
 * hamburger toggle is left to Craft untouched.
 *
 * Why capture phase: Craft binds click on each <summary> (bubble). A
 * capture listener on the drawer fires first; stopImmediatePropagation()
 * then prevents Craft's handler and preventDefault() stops the native
 * <details> toggle, so [open] is managed here with the timing the CSS
 * grid animation needs.
 *
 * Behaviour:
 *   * Tap a parent  -> expands beneath it; siblings at the same level close.
 *   * Tap it again  -> collapses.
 *   * One open per level (accordion). Works at any nesting depth.
 *
 * Vanilla JS. No dependencies. Mobile-only. Desktop dropdown untouched.
 */
(() => {
  const MOBILE_QUERY = '(max-width: 749px)';
  const COLLAPSE_MS = 360; // keep in sync with the CSS grid transition
  const MAIN_TOGGLE_ID = 'Details-menu-drawer-container';

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

  const getSummary = (details) => details.querySelector(':scope > summary');

  /**
   * Open a submenu. Sets [open] so the content renders at 0fr, then adds
   * `.is-expanded` on a later frame so the grid transition runs.
   * @param {HTMLDetailsElement} details
   */
  function expand(details) {
    details.setAttribute('open', '');
    requestAnimationFrame(() =>
      requestAnimationFrame(() => details.classList.add('is-expanded'))
    );
    const summary = getSummary(details);
    if (summary) summary.setAttribute('aria-expanded', 'true');
  }

  /**
   * Collapse a submenu (and any expanded descendants). Removes
   * `.is-expanded` to animate to 0fr, then drops [open] once the
   * transition has finished.
   * @param {HTMLDetailsElement} details
   */
  function collapse(details) {
    details.classList.remove('is-expanded');
    const summary = getSummary(details);
    if (summary) summary.setAttribute('aria-expanded', 'false');

    details.querySelectorAll('details.is-expanded').forEach((nested) => {
      nested.classList.remove('is-expanded');
      const nestedSummary = getSummary(nested);
      if (nestedSummary) nestedSummary.setAttribute('aria-expanded', 'false');
    });

    window.setTimeout(() => {
      if (details.classList.contains('is-expanded')) return; // re-opened meanwhile
      details.removeAttribute('open');
      details.querySelectorAll('details').forEach((nested) => {
        if (!nested.classList.contains('is-expanded')) nested.removeAttribute('open');
      });
    }, COLLAPSE_MS);
  }

  /**
   * Accordion rule: close open siblings at the same nesting level.
   * @param {HTMLDetailsElement} details
   */
  function collapseSiblings(details) {
    const li = details.parentElement;
    const ul = li && li.parentElement;
    if (!ul) return;
    ul.querySelectorAll(':scope > li > details.is-expanded').forEach((sibling) => {
      if (sibling !== details) collapse(sibling);
    });
  }

  /** Clear all accordion state (used when the whole drawer closes). */
  function resetAll(root) {
    root.querySelectorAll('details.is-expanded').forEach((details) => {
      details.classList.remove('is-expanded');
      const summary = getSummary(details);
      if (summary) summary.setAttribute('aria-expanded', 'false');
    });
  }

  function onClickCapture(event) {
    if (!isMobile()) return;

    const summary = event.target.closest('summary');
    if (!summary) return;

    const details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return;

    // Main hamburger: let Craft open/close the drawer, just reset state on close.
    if (details.id === MAIN_TOGGLE_ID) {
      if (details.hasAttribute('open')) resetAll(event.currentTarget);
      return;
    }

    // Own the submenu interaction.
    event.preventDefault();
    event.stopImmediatePropagation();

    const expanded =
      details.hasAttribute('open') && details.classList.contains('is-expanded');

    if (expanded) {
      collapse(details);
    } else {
      collapseSiblings(details);
      expand(details);
    }
  }

  function init(drawer) {
    drawer.addEventListener('click', onClickCapture, true);
  }

  const ready = (fn) =>
    document.readyState !== 'loading'
      ? fn()
      : document.addEventListener('DOMContentLoaded', fn);

  ready(() => {
    document.querySelectorAll('header-drawer, menu-drawer').forEach(init);
  });
})();
